import { parseSlotWidget, parseWidgetData, parsePatientObject } from "./parser"
import { getAssetPath, getCookie } from "@commonUtils"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { handleAuthentication } from "@containers/App/reducer"

const defaultState = {
    data: { slotData: {}, patientData: [], slotMixPanelData: null, patientMixPanelData: null },
    isSlotFetched: false,
    isSlotFetching: false,
    isPatientFetched: false,
    isPatientFetching: false,
    isPatientFetchingMore: false,
    isPatientFetchedMore: false,
    isPatientFetchedMoreError: false,
    dialogData: null,
    isSlotError: false,
    isPatientError: false,
    showLoader: false,
    patientAdded: false,
}

const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try that again!"

export const slotPageSlice = createSlice({
    name: "slotPage",
    initialState: defaultState,
    reducers: {
        reset: () => defaultState,
        fetchSelectedPatientData: (state, action) => {
            const updatedStateData = [
                {
                    type: state.data?.patientData[0].type,
                    data: { ...state.data?.patientData[0].data, memberList: action.payload },
                },
            ]
            state.data = { ...state.data, patientData: updatedStateData }
        },
        fetchPatientErrorMsg: (state, action) => {
            state.isPatientError = action.payload
            state.isPatientFetched = false
        },
        fetchMorePatientErrorMsg: (state, action) => {
            state.isPatientFetchedMoreError = action.payload
            state.isPatientFetchedMore = false
        },
        setPatientAdded: (state, action) => {
            state.patientAdded = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSlotSelectorData.fulfilled, (state, action) => {
            state.isSlotFetching = false
            state.isSlotFetched = true
            const parseData = parseSlotWidget(action.payload?.data, action.payload?.meta)
            state.data = {
                ...state.data,
                slotData: parseData,
                slotMixPanelData: action.payload?.data?.analytics_data_mixpanel,
            }
        })
        builder.addCase(fetchSlotSelectorData.pending, (state) => {
            state.isSlotFetching = true
            state.isSlotFetched = false
            state.isSlotError = false
            state.showLoader = false
            state.dialogData = null
            state.data = { ...state.data, slotData: {} }
        })
        builder.addCase(fetchSlotSelectorData.rejected, (state, action) => {
            state.isSlotFetching = false
            state.isSlotFetched = false
            state.isSlotError = action.payload.error
            state.data = { ...state.data, slotData: {} }
        })
        builder.addCase(submitSlotCollectionTime.fulfilled, (state) => {
            state.showLoader = false
        })

        builder.addCase(submitSlotCollectionTime.pending, (state) => {
            state.showLoader = true
        })
        builder.addCase(submitSlotCollectionTime.rejected, (state) => {
            state.showLoader = false
        })
        builder.addCase(removeVas.pending, (state) => {
            state.showLoader = true
            state.isSlotError = false
        })
        builder.addCase(removeVas.fulfilled, (state) => {
            state.showLoader = false
            state.isSlotError = false
        })
        builder.addCase(removeVas.rejected, (state, action) => {
            state.showLoader = false
            state.isSlotError = action.payload.error
        })
        // Patient Action
        builder.addCase(fetchPatientData.fulfilled, (state, action) => {
            state.isPatientFetching = false
            state.isPatientFetched = true
            state.data = {
                ...state.data,
                patientData: action.payload?.widgetsData,
                patientMixPanelData: action.payload?.mixPanelData,
            }
            state.metaData = action.payload?.metaData
        })
        builder.addCase(fetchPatientData.pending, (state) => {
            state.isPatientFetching = true
            state.isPatientFetched = false
            state.metaData = {}
            state.isPatientError = false
            state.showLoader = false
            state.isPatientFetchedMoreError = false
        })
        builder.addCase(fetchPatientData.rejected, (state, action) => {
            state.isPatientFetching = false
            state.isPatientFetched = false
            state.isPatientError = action.payload.error
        })
        builder.addCase(fetchMorePatientData.fulfilled, (state, action) => {
            const morePatientData = [
                {
                    type: state.data?.patientData[0].type,
                    data: {
                        ...state.data?.patientData[0].data,
                        memberList: state.data?.patientData[0]?.data?.memberList.concat(
                            action.payload?.widgetsData[0]?.data?.memberList
                        ),
                    },
                },
            ]
            state.isPatientFetchingMore = false
            state.isPatientFetchedMore = true
            state.data = { ...state.data, patientData: morePatientData }
            state.metaData = action.payload?.metaData
        })
        builder.addCase(fetchMorePatientData.pending, (state) => {
            state.isPatientFetchingMore = true
            state.isPatientFetchedMore = false
            state.isPatientFetchedMore = false
            state.showLoader = false
        })
        builder.addCase(fetchMorePatientData.rejected, (state, action) => {
            state.isPatientFetchingMore = false
            state.isPatientFetchedMore = false
            state.isPatientFetchedMoreError = action.payload.error
        })
        builder.addCase(editPatientData.pending, (state) => {
            state.showLoader = false
        })
        builder.addCase(editPatientData.fulfilled, (state, action) => {
            const { memberList, maxPatients } = state.data.patientData[0].data
            const selectedPatients = memberList?.filter((patient) => patient.isSelected === true).length
            const parsePatientData = parsePatientObject(
                action.payload.data,
                selectedPatients + 1,
                maxPatients
            )
            const oldPatientList = memberList.filter(
                (patient) => patient.patientId !== parsePatientData.patientId
            )
            const updatedData = [parsePatientData].concat(oldPatientList)
            const updatedStateData = [
                {
                    type: state.data.patientData[0].type,
                    data: { ...state.data.patientData[0].data, memberList: updatedData },
                },
            ]
            state.data = { ...state.data, patientData: updatedStateData }
            state.showLoader = false
        })

        builder.addCase(editPatientData.rejected, (state) => {
            state.showLoader = false
        })
        builder.addCase(removePatientData.pending, (state) => {
            state.showLoader = false
            state.isPatientError = false
        })
        builder.addCase(removePatientData.fulfilled, (state, action) => {
            const newPatientList = state.data.patientData[0].data.memberList.filter(
                (patient) => patient.patientId !== action.payload.id
            )
            const updatedStateData = [
                {
                    type: state.data?.patientData[0].type,
                    data: { ...state.data.patientData[0].data, memberList: newPatientList },
                },
            ]
            state.data = { ...state.data, patientData: updatedStateData }
            state.showLoader = false
        })

        builder.addCase(removePatientData.rejected, (state) => {
            state.showLoader = false
        })
        builder.addCase(addNewPatientData.pending, (state) => {
            state.showLoader = true
            state.error = false
        })
        builder.addCase(addNewPatientData.fulfilled, (state, action) => {
            const { memberList, maxPatients } = state.data.patientData[0].data
            const selectedPatients = memberList?.filter((patient) => patient.isSelected === true).length
            const parsePatientData = parsePatientObject(
                action.payload.data,
                selectedPatients + 1,
                maxPatients
            )
            const patientList = [parsePatientData].concat(memberList)
            const updatedStateData = [
                {
                    type: state.data?.patientData[0].type,
                    data: { ...state.data?.patientData[0].data, memberList: patientList },
                },
            ]
            state.data = { ...state.data, patientData: updatedStateData }
            state.showLoader = false
        })

        builder.addCase(addNewPatientData.rejected, (state) => {
            state.showLoader = false
        })
        builder.addCase(submitSelectedPatientData.pending, (state) => {
            state.showLoader = true
        })
        builder.addCase(submitSelectedPatientData.fulfilled, (state) => {
            state.showLoader = false
        })

        builder.addCase(submitSelectedPatientData.rejected, (state) => {
            state.showLoader = false
        })
        builder.addCase(initAddToCartPatient.fulfilled, (state) => {
            state.showLoader = false
            state.error = false
        })

        builder.addCase(initAddToCartPatient.rejected, (state) => {
            state.showLoader = false
            state.error = true
        })
    },
})

export const fetchSlotSelectorData = createAsyncThunk(
    "patientPage/fetchSlotSelectorData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const city = getCookie("city")
        try {
            const res = await api.get(`/api/v4/carts/slots?city=${city}&types=labs`)
            return Promise.resolve(res)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401)) {
                dispatch(handleAuthentication())
            } else if (err.status_code === 400) {
                window.location.href = "/labs/cart"
            }
            return rejectWithValue(err)
        }
    }
)

export const submitSlotCollectionTime = createAsyncThunk(
    "patientPage/submitSlotCollectionTime",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { slot, successCallback, errorCallback } = _arg
        const payload = {
            slot_id: slot.slotId,
            collection_time: slot.timeEpochSlot,
        }
        try {
            const res = await api.put(`/api/v4/carts/collection-time?types=labs`, payload)
            if (res.is_success) {
                successCallback(res?.data)
            }
            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401)) {
                dispatch(handleAuthentication())
            } else if (err.status_code === 400 && err?.error?.dialog_data?.type === "other_lab") {
                const errorData = err?.error?.dialog_data?.data
                const dialogData = {
                    imageUrl: getAssetPath("svgs/icons/new_alert_48px.svg"),
                    heading: errorData?.header,
                    subHeading: errorData?.description,
                    primaryCtaLabel: errorData?.okay_cta?.text,
                    imageDimensions: { height: "48px", width: "48px" },
                }
                errorCallback({ dialogData, errorMsg: errorData.header })
            } else {
                errorCallback({ errorMsg: "Something went wrong" })
            }
            return rejectWithValue()
        }
    }
)

export const removeVas = createAsyncThunk(
    "patientPage/removeVas",
    async (_arg, { extra, rejectWithValue }) => {
        const { api } = extra
        const { vasList, successCallback, errorCallback } = _arg
        try {
            if (vasList && successCallback) {
                let vasToRemove = {}
                vasList?.map((vas) => {
                    vasToRemove[vas] = false
                })
                const res = await api.put("api/v4/carts/preferences?types=labs", vasToRemove)
                if (res?.is_success) {
                    successCallback()
                    return Promise.resolve(res)
                }
            }
        } catch (err) {
            if (typeof errorCallback === "function") {
                errorCallback(GENERIC_ERROR_MESSAGE)
            }
            return rejectWithValue(err)
        }
    }
)

// Patient Data

export const fetchMorePatientData = createAsyncThunk(
    "patientPage/fetchMorePatientData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payload, successCallback } = _arg
        try {
            const res = await api.get(
                `/api/v4/carts/patients?types=labs&page_number=${payload.pageNumber}&page_size=${payload.pageSize}`
            )
            const { widgetsData, metaData } = parseWidgetData(res?.data, res?.meta)
            if (typeof successCallback === "function") {
                successCallback(widgetsData)
            }

            return Promise.resolve({ widgetsData, metaData })
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401)) {
                dispatch(handleAuthentication())
            } else if (err.status_code === 400) {
                window.location.href = "/labs/cart"
            }
            return rejectWithValue(err)
        }
    }
)

export const fetchPatientData = createAsyncThunk(
    "patientPage/fetchPatientData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { successCallback } = _arg
        try {
            const res = await api.get("/api/v4/carts/patients?types=labs&page_number=0&page_size=10")
            const { widgetsData, metaData } = parseWidgetData(res?.data, res?.meta)
            if (typeof successCallback === "function") {
                successCallback(widgetsData)
            }
            return Promise.resolve({
                widgetsData,
                metaData,
                mixPanelData: res?.data?.data?.analytics_data_mixpanel,
            })
        } catch (err) {
            const onServer = typeof window === "undefined"

            if (!onServer && (err.status_code === 403 || err.status_code === 401)) {
                dispatch(handleAuthentication())
            } else if (err.status_code === 400) {
                window.location.href = "/labs/cart"
            }
            return rejectWithValue(err)
        }
    }
)

export const removePatientData = createAsyncThunk(
    "patientPage/removePatientData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { patientId, successCallback, errorCallback } = _arg
        try {
            const res = await api.delete(`/api/v4/user/patients/${patientId}?types=labs`)
            if (res.is_success) {
                if (typeof successCallback === "function") {
                    successCallback()
                }
            }

            return Promise.resolve(res.data)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (typeof errorCallback === "function") {
                errorCallback(GENERIC_ERROR_MESSAGE)
            }

            if (!onServer && (err.status_code === 403 || err.status_code === 401)) {
                dispatch(handleAuthentication())
            } else if (err.status_code === 400) {
                window.location.href = "/labs/cart"
            }
            return rejectWithValue(err)
        }
    }
)

export const editPatientData = createAsyncThunk(
    "patientPage/eidtPatientData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { patientData, successCallback, errorCallback } = _arg
        const payload = {
            name: patientData?.name,
            gender: patientData?.gender == "Male" ? "m" : "f",
            date_of_birth: patientData?.dateOfBirth,
            types: "labs",
        }
        try {
            const res = await api.put(`/api/v4/user/patients/${patientData.patientId}`, payload)
            if (res.is_success) {
                if (typeof successCallback === "function") {
                    successCallback("Patient details updated successfully")
                }
            }

            return Promise.resolve(res)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (typeof errorCallback === "function") {
                errorCallback(GENERIC_ERROR_MESSAGE)
            }

            if (!onServer && (err.status_code === 403 || err.status_code === 401)) {
                dispatch(handleAuthentication())
            } else if (err.status_code === 400) {
                window.location.href = "/labs/cart"
            }
            return rejectWithValue(err)
        }
    }
)

export const addNewPatientData = createAsyncThunk(
    "patientPage/addNewPatientData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { payloadData, successCallback, errorCallback } = _arg
        const payload = {
            name: payloadData?.name,
            gender: payloadData?.gender == "Male" ? "m" : "f",
            date_of_birth: payloadData?.dateOfBirth,
            types: "labs",
        }
        try {
            const res = await api.post(`/api/v4/user/patients?type=labs`, payload)
            if (res.is_success) {
                if (typeof successCallback === "function") {
                    successCallback("New patient saved", res.data)
                }
            }

            return Promise.resolve(res)
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (typeof errorCallback === "function") {
                if (err.status_code === 400) {
                    errorCallback(err.error?.message || GENERIC_ERROR_MESSAGE)
                } else {
                    errorCallback(GENERIC_ERROR_MESSAGE)
                }
            }

            if (!onServer && (err.status_code === 403 || err.status_code === 401)) {
                dispatch(handleAuthentication())
            }
            return rejectWithValue(err)
        }
    }
)

export const initAddToCartPatient = createAsyncThunk(
    "patientPage/addToCartPatient",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { selectedPatientId, successCallback, errorCallback } = _arg
        const payload = {
            patient_ids: selectedPatientId,
        }
        try {
            const res = await api.put("/api/v4/carts/preferences?types=labs", payload)
            if (res.is_success) {
                if (selectedPatientId.length > 0) dispatch(slotPageSlice.actions.setPatientAdded(true))
                if (typeof successCallback === "function") {
                    successCallback()
                }
            }

            return Promise.resolve(res.data)
        } catch (err) {
            if (typeof errorCallback === "function") {
                errorCallback(GENERIC_ERROR_MESSAGE)
            }
            return rejectWithValue(err)
        }
    }
)

export const submitSelectedPatientData = createAsyncThunk(
    "patientPage/submitSelectedPatientData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { selectedPatientId, successCallback, errorCallback } = _arg
        const payload = {
            patient_ids: selectedPatientId,
        }
        try {
            const res = await api.put("/api/v4/carts/preferences?types=labs", payload)
            if (res.is_success) {
                if (selectedPatientId.length > 0) dispatch(slotPageSlice.actions.setPatientAdded(true))
                if (typeof successCallback === "function") {
                    successCallback()
                }
            }

            return Promise.resolve(res.data)
        } catch (err) {
            if (typeof errorCallback === "function") {
                errorCallback(GENERIC_ERROR_MESSAGE)
            }
            return rejectWithValue(err)
        }
    }
)

export const slotPageReducer = slotPageSlice.reducer
export const { reset, fetchSelectedPatientData, fetchMorePatientErrorMsg, fetchPatientErrorMsg } =
    slotPageSlice.actions
